(() => {
  const source = document.currentScript;
  if (!source) return;

  // This head script is deferred. Track the end of initial deferred execution
  // even if stylesheet recovery finishes while other page resources still load.
  let initialScriptsPending = document.readyState !== 'complete';
  document.addEventListener('DOMContentLoaded', () => { initialScriptsPending = false; }, { once: true });

  // Keep merchant-authored custom Liquid and later recommendation/editor markup supported.
  const legacyTags = [
    'hoto-header-m', 'hoto-header-pc-nav', 'hoto-slide-reviews', 'hoto-slide-blogs',
    'hoto-slide-brands', 'hoto-banner', 'hoto-small-list-image-info', 'hoto-big-media-info',
    'hoto-slide-image-info', 'hoto-slide-video-info', 'hoto-slide-media-product', 'hoto-line-slide-image',
  ];
  const selector = legacyTags.join(',');
  const styleSelector = legacyTags.slice(2).map((tag) => `[class*="${tag}"]`).join(',');
  const resources = new Map();
  let loading = null;
  let fadeLoading = null;
  let styleLoading = null;
  let legacyReady = false;
  let styleReady = false;
  let fadeReady = false;
  let observer;
  const contains = (root, selector) => root.matches?.(selector) || root.querySelector(selector);
  const stopIfReady = () => { if (legacyReady && styleReady && fadeReady) observer?.disconnect(); };

  function stylesheetReady(element) {
    if (!element.sheet) return false;
    // These component stylesheets all contain rules. Chrome can leave an empty
    // sheet on HTTP failure; opaque CDN sheets cannot expose their rules.
    try { return element.sheet.cssRules.length > 0; }
    catch { return true; }
  }

  function loadResource(url, stylesheet = false, glide = false) {
    if (resources.has(url)) return resources.get(url);
    const existing = [...document.head.querySelectorAll(stylesheet ? 'link[rel="stylesheet"]' : 'script[src]')]
      .find((element) => element.getAttribute(stylesheet ? 'href' : 'src') === url);
    const isTextFadeScript = !stylesheet && url === source.dataset.textFadeScript;
    const scriptReady = () => !isTextFadeScript || !!customElements.get('hoto-text-fade');
    // Initial styles are synchronous head links. A script tag alone is not proof
    // that text-fade executed: failed requests leave their original tag in place.
    if (existing && (stylesheet ? stylesheetReady(existing) : scriptReady())) return Promise.resolve();
    if (existing && isTextFadeScript && initialScriptsPending) {
      // The loader can run before the following deferred text-fade script. Await
      // it rather than duplicating it. DOMContentLoaded also catches an error
      // that occurred before these listeners were attached.
      const promise = new Promise((resolve, reject) => {
        const finish = () => {
          existing.removeEventListener('load', finish);
          existing.removeEventListener('error', finish);
          document.removeEventListener('DOMContentLoaded', finish);
          resources.delete(url);
          if (scriptReady()) resolve();
          else {
            existing.remove();
            loadResource(url).then(resolve, reject);
          }
        };
        existing.addEventListener('load', finish, { once: true });
        existing.addEventListener('error', finish, { once: true });
        document.addEventListener('DOMContentLoaded', finish, { once: true });
      });
      resources.set(url, promise);
      return promise;
    }
    // Failed initial styles/scripts can be replaced after parsing.
    existing?.remove();

    const promise = new Promise((resolve, reject) => {
      const element = document.createElement(stylesheet ? 'link' : 'script');
      if (stylesheet) {
        element.rel = 'stylesheet';
        element.href = url;
      } else {
        element.src = url;
      }
      element.onload = () => scriptReady() ? resolve() : element.onerror();
      element.onerror = () => {
        element.remove();
        resources.delete(url);
        reject(new Error(`Could not load HOTO component asset: ${url}`));
      };
      if (stylesheet) {
        // Glide stays before shared overrides; legacy component rules stay after them.
        const anchor = glide ? source : document.head.querySelector('meta[name="hoto-component-style-anchor"]');
        document.head.insertBefore(element, anchor);
      } else {
        document.head.appendChild(element);
      }
    });
    resources.set(url, promise);
    return promise;
  }

  function loadForRenderedElements(root = document) {
    if (!loading && !legacyReady && contains(root, selector)) {
      // All measurement styles must finish before hoto.js upgrades carousel elements.
      loading = Promise.all([
        loadResource(source.dataset.glideStyle, true, true),
        loadResource(source.dataset.legacyStyle, true),
        loadResource(source.dataset.glideScript),
      ]).then(() => loadResource(source.dataset.hotoScript)).then(() => {
        legacyReady = styleReady = true;
        stopIfReady();
      }).catch((error) => { loading = null; console.warn(error.message); });
    } else if (!loading && !styleLoading && !styleReady && contains(root, styleSelector)) {
      styleLoading = loadResource(source.dataset.legacyStyle, true).then(() => {
        styleReady = true;
        stopIfReady();
      }).catch((error) => { styleLoading = null; console.warn(error.message); });
    }
    if (!fadeLoading && !fadeReady && contains(root, 'hoto-text-fade')) {
      fadeLoading = loadResource(source.dataset.textFadeStyle, true)
        .then(() => loadResource(source.dataset.textFadeScript)).then(() => {
          fadeReady = true;
          stopIfReady();
        }).catch((error) => { fadeLoading = null; console.warn(error.message); });
    }
  }

  function start() {
    // Section Rendering API insertions have no editor event. Scan only new subtrees,
    // skip resources already available/loading, and stop once both bundles are ready.
    observer = new MutationObserver((records) => {
      for (const record of records) for (const node of record.addedNodes) {
        if (node.nodeType === 1) loadForRenderedElements(node);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    loadForRenderedElements();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', (event) => loadForRenderedElements(event.target));
})();
